import Button from '@app/react-common/components/button/Button';
import { ThreeDotMenuItem } from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { SetFallbackRateModal } from '../fallback-rate/set-fallback-rate/SetFallbackRateModal';
import { RemoveFallbackRateModal } from '../fallback-rate/remove-fallback-rate/RemoveFallbackRateModal';
import { Shippings } from '@app/pages/checkout-app/types';
import { useAppContext } from '@app/react-common/AppContext';
import { Checkout } from '../../models/Checkout';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { rateHasAnyDiscount } from '@app/pages/checkout-app/utils';
import { FallbackRequest } from '../../models/FallbackRequest';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { ICheckoutActions } from './../../models/ICheckoutActions';
import { DuplicateMethodToAnotherCheckoutGroupModal } from './duplicate-method-modal/DuplicateMethodToAnotherCheckoutGroupModal';
import { RateType } from '../../constants/RateType';
import { DeliveryMethodsFilterBar, DeliveryMethodsTableHeader, DeliveryMethodRow } from './components';
import { BulkEditModal } from './bulk-edit-modal/BulkEditModal';
import { useDeliveryMethodsState } from './hooks/useDeliveryMethodsState';
import { useAllShippingMethods } from './hooks/useAllShippingMethods';
import { mapShippingToSelectedMethodInfo } from './utils/mapToSelectedMethodInfo';
import { MIN_METHODS_FOR_BULK_EDIT } from './utils/consts';
import { Checkout as CheckoutInterfaces } from '@pdqdev/pdq-shared/dist/interfaces';
import useCheckoutGroupSettings from '../checkout-settings-modal/hooks/useCheckoutGroupSettings';
import { GroupByControl } from './group-by/GroupByControl';
import { GroupedDeliveryMethods } from './group-by/GroupedDeliveryMethods';
import { CombinedNameBanner } from './group-by/CombinedNameBanner';
import { upsertCategoryFallback } from './group-by/categoryGrouping';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { DeliveryMethodSections } from './sections/DeliveryMethodSections';
import { useDeliveryMethodDisplayOrder } from './sections/useDeliveryMethodDisplayOrder';
import {
  UNSECTIONED_SECTION_KEY,
  addSection,
  createDeliveryMethodSectionId,
  moveDeliveryMethodsToEndOfSection,
  sortDeliveryMethodsByManualOrder,
} from './sections/deliveryMethodDisplayOrder';
import { MoveToSectionTarget } from './bulk-edit-modal/views/MoveToSectionView';
import { TYPE_OPTIONS, STATUS_OPTIONS } from './components/DeliveryMethodsFilterBar';

interface DeliveryMethodsListProps {
  checkoutGroups: Checkout[];
  activeCheckout: Checkout;
  checkoutActions: ICheckoutActions;
  setRerender: React.Dispatch<React.SetStateAction<boolean>>;
  onDataRefresh?: () => void;
  onInvalidateGroups?: (groupIds: number[]) => void;
  refetchGroupShippings?: (groupId: number) => Promise<void>;
  fetchAllCheckoutShippings?: () => Promise<void>;
  isLoadingAllGroups?: boolean;
  onUnsavedDisplayOrderChangesChange?: (hasUnsavedDisplayOrderChanges: boolean) => void;
}

export const DeliveryMethodsList = (props: DeliveryMethodsListProps) => {
  const fallbackMethods = props.activeCheckout.checkoutShippings.filter((x) => x.fallbackMethod) ?? [];
  const [setFallbackModelOpen, setSetFallbackModelOpen] = useState(false);
  const [editFallbackRateModalOpen, setEditFallbackRateModalOpen] = useState(false);
  const [deleteDeliveryMethod, setDeleteDeliveryMethod] = useState<{ id: string; name: string } | null>(null);
  const [removeFallbackModalOpen, setRemoveFallbackModalOpen] = useState(false);
  const [enabledFallbackToastMessage, setEnabledFallbackToastMessage] = useState<string | null>(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [deliveryMethodToDuplicate, setDeliveryMethodToDuplicate] = useState<{ id: string; name: string } | null>(null);

  const { angularRedirect, shop$ } = useAppContext();
  const { showToast } = useAppContext();

  const {
    shopCurrency,
    shopCurrencyCode,
    shopCountryCode,
    enableCategoryBasedShipping,
    selectedIds,
    setSelectedIds,
    searchQuery,
    setSearchQuery,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    bulkEditModalOpen,
    setBulkEditModalOpen,
    handleSelectItem,
    filterMethod,
  } = useDeliveryMethodsState(shop$);

  const deliveryMethods = useMemo(
    () => sortDeliveryMethodsByManualOrder(props.activeCheckout.checkoutShippings.filter((x) => !x.fallbackMethod)),
    [props.activeCheckout.checkoutShippings],
  );

  const deliveryMethodById = useMemo(
    () => new Map(deliveryMethods.map((deliveryMethod) => [String(deliveryMethod.id), deliveryMethod])),
    [deliveryMethods],
  );

  const {
    displayOrder,
    hasUnsavedDisplayOrderChanges,
    isSavingDisplayOrder,
    applyDisplayOrderChange,
    saveDisplayOrder,
    discardDisplayOrderChanges,
    collapsedSectionKeys,
    toggleSectionCollapsed,
  } = useDeliveryMethodDisplayOrder({
    checkoutGroupId: props.activeCheckout.id,
    savedSections: props.activeCheckout.deliveryMethodSections,
    orderableDeliveryMethods: deliveryMethods,
    checkoutActions: props.checkoutActions,
  });
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const hasUnsavedDisplayOrderChangesOrSectionBeingCreated = hasUnsavedDisplayOrderChanges || isCreatingSection;

  useEffect(() => {
    props.onUnsavedDisplayOrderChangesChange?.(hasUnsavedDisplayOrderChangesOrSectionBeingCreated);
  }, [hasUnsavedDisplayOrderChangesOrSectionBeingCreated]);

  useEffect(() => () => props.onUnsavedDisplayOrderChangesChange?.(false), []);

  const isFilterActive =
    !!searchQuery || typeFilter.value !== TYPE_OPTIONS[0].value || statusFilter.value !== STATUS_OPTIONS[0].value;

  const moveSelectedMethodsToSection = (target: MoveToSectionTarget) => {
    const selectedMethodIds = [...selectedIds].map(String).filter((methodId) => deliveryMethodById.has(methodId));
    applyDisplayOrderChange((currentDisplayOrder) => {
      if (target.kind === 'new') {
        const newSectionId = createDeliveryMethodSectionId();
        return moveDeliveryMethodsToEndOfSection(
          addSection(currentDisplayOrder, { id: newSectionId, name: target.sectionName }),
          selectedMethodIds,
          newSectionId,
        );
      }
      return moveDeliveryMethodsToEndOfSection(
        currentDisplayOrder,
        selectedMethodIds,
        target.sectionId ?? UNSECTIONED_SECTION_KEY,
      );
    });
    setSelectedIds(new Set());
  };

  const filteredDeliveryMethods = useMemo(() => {
    return deliveryMethods.filter(filterMethod);
  }, [deliveryMethods, filterMethod]);

  const { checkoutGroupSettings, setCheckoutGroupSettings } = useCheckoutGroupSettings(props.activeCheckout.id);
  const categoryConfig = checkoutGroupSettings?.categoryBasedShippingConfig ?? null;
  const isGrouped = !!categoryConfig?.enabled;

  const persistCategoryConfig = async (
    nextConfig: CheckoutInterfaces.CategoryBasedShippingConfig | null,
    refreshGroupsList = false,
  ) => {
    const nextSettings = { ...(checkoutGroupSettings ?? ({} as any)), categoryBasedShippingConfig: nextConfig };
    setCheckoutGroupSettings(nextSettings);
    try {
      await props.checkoutActions.saveCheckoutMethodGroupSettings(props.activeCheckout.id, nextSettings);
      // Only the enable/disable toggle affects the sidebar "Grouped" badge — refetch the groups
      // list then so it updates without a page reload (skipped for name/fallback edits).
      if (refreshGroupsList) {
        props.onDataRefresh?.();
      }
    } catch {
      // Save failures are surfaced by the global API error handler; swallow here to avoid an
      // unhandled promise rejection.
    }
  };

  const applyGroupBy = (groupBy: CheckoutInterfaces.CategoryShippingGroupBy) => {
    void persistCategoryConfig(
      {
        enabled: true,
        groupBy,
        combinedMethodName: categoryConfig?.combinedMethodName ?? '',
        categoryFallbacks: categoryConfig?.categoryFallbacks ?? [],
      },
      true,
    );
  };

  const clearGroupBy = () => {
    if (categoryConfig) {
      void persistCategoryConfig({ ...categoryConfig, enabled: false }, true);
    }
  };

  const handleCombinedNameChange = (combinedMethodName: string) => {
    if (categoryConfig) {
      void persistCategoryConfig({ ...categoryConfig, combinedMethodName });
    }
  };

  const handleCategoryFallbackChange = (fallback: CheckoutInterfaces.CategoryShippingFallback) => {
    if (categoryConfig) {
      void persistCategoryConfig({
        ...categoryConfig,
        categoryFallbacks: upsertCategoryFallback(categoryConfig, fallback),
      });
    }
  };

  const allFilteredSelected =
    filteredDeliveryMethods.length > 0 && filteredDeliveryMethods.every((method) => selectedIds.has(method.id));

  const someFilteredSelected =
    filteredDeliveryMethods.some((method) => selectedIds.has(method.id)) && !allFilteredSelected;

  const selectedMethodsInfo = useMemo(() => {
    return deliveryMethods
      .filter((method) => selectedIds.has(method.id))
      .map((method) => mapShippingToSelectedMethodInfo(method, props.activeCheckout.name || '', shopCurrencyCode));
  }, [deliveryMethods, selectedIds, props.activeCheckout.name, shopCurrencyCode]);

  const allShippingMethods = useAllShippingMethods(props.checkoutGroups);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelectedIds = new Set(selectedIds);
      filteredDeliveryMethods.forEach((method) => newSelectedIds.add(method.id));
      setSelectedIds(newSelectedIds);
    } else {
      const newSelectedIds = new Set(selectedIds);
      filteredDeliveryMethods.forEach((method) => newSelectedIds.delete(method.id));
      setSelectedIds(newSelectedIds);
    }
  };

  const handleBulkEditClick = async () => {
    const hasUnloadedGroups = props.checkoutGroups.some(
      (group) => !group.checkoutShippings || group.checkoutShippings.length === 0,
    );
    if (hasUnloadedGroups && props.fetchAllCheckoutShippings) {
      try {
        await props.fetchAllCheckoutShippings();
      } catch (error) {
        showToast({ message: 'Failed to load delivery methods', mode: 'error' });
        return;
      }
    }
    setBulkEditModalOpen(true);
  };

  useEffect(() => {
    if (enabledFallbackToastMessage) {
      showToast({ message: enabledFallbackToastMessage, mode: 'info' });
      setEnabledFallbackToastMessage(null);
    }
  }, [enabledFallbackToastMessage]);

  const domesticFallback = fallbackMethods.find((method) => !method.metadata?.isInternational);
  const internationalFallback = fallbackMethods.find((method) => method.metadata?.isInternational);

  const transformedFallbackMethods: FallbackRequest[] = fallbackMethods.map((method) => ({
    id: parseInt(method.id),
    name: method.name,
    rate: method.rate,
    disablePOBoxCheck: method.metadata?.disableShippingAddressConditionCheck || false,
    isInternational: method.metadata?.isInternational || false,
    enabled: method.enabled,
    rateType: method.metadata?.fallbackRateType === RateType.PERCENTAGE ? RateType.PERCENTAGE : RateType.FIXED,
  }));

  const getMenuItems = (deliveryMethod: Shippings): ThreeDotMenuItem[] => {
    const actions: ThreeDotMenuItem[] = [
      {
        label: 'Edit',
        onClick: () => {
          angularRedirect(`/convert/checkout-groups/methods/edit?checkoutId=${deliveryMethod.id}`);
        },
      },
      {
        label: 'Duplicate',
        onClick: () => {
          angularRedirect(
            `/convert/checkout-groups/methods/new?did=${deliveryMethod.id}&collectionId=${props.activeCheckout.id}`,
          );
        },
      },
    ];

    if (!rateHasAnyDiscount(deliveryMethod)) {
      actions.push({
        label: 'Duplicate to another group',
        onClick: () => {
          setDeliveryMethodToDuplicate({ id: deliveryMethod.id, name: deliveryMethod.name });
          setDuplicateModalOpen(true);
        },
      });
    }

    actions.push({
      label: 'Delete',
      onClick: () => {
        setDeleteDeliveryMethod({ id: deliveryMethod.id, name: deliveryMethod.name });
      },
    });

    return actions;
  };

  const renderMethodRow = (deliveryMethod: Shippings, dragHandleProps?: DraggableProvidedDragHandleProps | null) => (
    <DeliveryMethodRow
      key={deliveryMethod.id}
      deliveryMethod={deliveryMethod}
      isSelected={selectedIds.has(deliveryMethod.id)}
      onSelectChange={(checked) => handleSelectItem(deliveryMethod.id, checked)}
      onToggleEnabled={() => {
        props.checkoutActions.toggleEnableDeliveryMethod(parseInt(deliveryMethod.id), !deliveryMethod.enabled, () => {
          props.setRerender((prev: boolean) => !prev);
        });
      }}
      menuItems={getMenuItems(deliveryMethod)}
      shopCurrency={shopCurrency}
      showCheckbox
      dragHandleProps={dragHandleProps}
    />
  );

  const headerPortalElement = document.getElementById('header-portal-content');

  const showsSectionsAsSeparateCards =
    !(isGrouped && categoryConfig) && (displayOrder.sections.length > 0 || isCreatingSection);
  return (
    <div className="delivery-methods-list">
      <div className="delivery-methods-list__header">
        <h2>
          Delivery methods
          {isGrouped && <span className="delivery-methods-list__grouped-badge">Grouped</span>}
        </h2>
        <div className="delivery-methods-list__header__actions">
          <Button
            text="+ Add Delivery Method"
            onClick={() => {
              angularRedirect(
                `/convert/checkout-groups/methods/new${props.activeCheckout.id ? '?collectionId=' + props.activeCheckout.id : ''}`,
              );
            }}
            size="medium"
            variant="tertiary"
          />
          {!isGrouped && (
            <span
              className="delivery-methods-list__header__add-section"
              onMouseDown={(mouseDownEvent) => isCreatingSection && mouseDownEvent.preventDefault()}>
              <Button
                text="+ Add Section"
                onClick={() => setIsCreatingSection(true)}
                size="medium"
                variant="tertiary"
              />
            </span>
          )}
          {selectedIds.size > 0 &&
            (selectedIds.size < MIN_METHODS_FOR_BULK_EDIT || props.isLoadingAllGroups ? (
              <Tooltip
                tooltipText={
                  props.isLoadingAllGroups
                    ? 'Loading delivery methods...'
                    : 'Select at least 2 delivery methods to bulk edit'
                }>
                <Button
                  text={props.isLoadingAllGroups ? 'Loading...' : `Bulk Edit (${selectedIds.size})`}
                  onClick={() => {}}
                  size="medium"
                  variant="primary"
                  disabled={true}
                />
              </Tooltip>
            ) : (
              <Button
                text={`Bulk Edit (${selectedIds.size})`}
                onClick={handleBulkEditClick}
                size="medium"
                variant="primary"
              />
            ))}
        </div>
      </div>
      <div>
        <span className="delivery-methods-list__divider-info">Manage which shipping options show in checkout</span>
      </div>
      <DeliveryMethodsFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        groupByControl={
          enableCategoryBasedShipping ? (
            <GroupByControl
              isGrouped={isGrouped}
              groupBy={categoryConfig?.groupBy}
              onApply={applyGroupBy}
              onClear={clearGroupBy}
            />
          ) : null
        }
      />
      {isGrouped && (
        <WarningBanner
          title="Pay attention"
          content="all methods in the same category must use the same name shown in checkout"
          hideCloseButton={true}
          variant="attention"
        />
      )}
      {isGrouped && (
        <WarningBanner
          title="Pay attention"
          content="when grouping by variant metafield, products without a variant metafield fall back to their product metafield"
          hideCloseButton={true}
          variant="attention"
        />
      )}
      {isGrouped && categoryConfig && (
        <CombinedNameBanner value={categoryConfig.combinedMethodName} onChange={handleCombinedNameChange} />
      )}
      <div
        className={`delivery-methods-list__main-content ${
          showsSectionsAsSeparateCards ? 'delivery-methods-list__main-content--separate-cards' : ''
        }`}>
        <div className="delivery-methods-list__main-content__summary-card">
          <div className="delivery-methods-list__main-content__fallback">
            <div className="delivery-methods-list__main-content__fallback__container">
              <div className="delivery-methods-list__main-content__fallback__title">
                <h4>Fallback rate</h4>
                <Tooltip tooltipText={'Default price if no other rule applies'}>
                  <i className="icon icon-more_information" />
                </Tooltip>
              </div>
              <div className="delivery-methods-list__main-content__fallback__rates">
                <p className="delivery-methods-list__main-content__fallback__info">
                  {!domesticFallback || domesticFallback?.enabled === false
                    ? 'Domestic delivery - Disabled'
                    : `${domesticFallback?.metadata?.fallbackRateType === RateType.PERCENTAGE ? '%' : shopCurrency}${domesticFallback?.rate ?? 0} per domestic delivery` +
                      (domesticFallback?.metadata?.disableShippingAddressConditionCheck ? ' (excluding PO boxes)' : '')}
                </p>
                <span className="delivery-methods-list__main-content__fallback__info">|</span>
                <p className="delivery-methods-list__main-content__fallback__info">
                  {!internationalFallback || internationalFallback?.enabled === false
                    ? 'International delivery - Disabled'
                    : `${internationalFallback?.metadata?.fallbackRateType === RateType.PERCENTAGE ? '%' : shopCurrency}${internationalFallback?.rate ?? 0} per international delivery` +
                      (internationalFallback?.metadata?.disableShippingAddressConditionCheck
                        ? ' (excluding PO boxes)'
                        : '')}
                </p>
              </div>
            </div>
            <div className="delivery-methods-list__main-content__actions">
              {setFallbackModelOpen && (
                <SetFallbackRateModal
                  isOpen={setFallbackModelOpen}
                  fallbackMethods={transformedFallbackMethods}
                  mode={'create'}
                  onClose={() => {
                    setSetFallbackModelOpen(false);
                  }}
                  onCreateSuccess={async (requests: FallbackRequest[]) => {
                    try {
                      await Promise.all(
                        requests.map(async (request) => {
                          request.checkoutId = props.activeCheckout.id;
                          await props.checkoutActions.enableFallback(request);
                        }),
                      );
                      setEnabledFallbackToastMessage('Fallback rate is on');
                    } catch (error) {
                      setEnabledFallbackToastMessage('Failed to enable fallback rates. Please try again.');
                    } finally {
                      setSetFallbackModelOpen(false);
                    }
                  }}
                  currencySign={shopCurrency}
                />
              )}

              {editFallbackRateModalOpen && (
                <SetFallbackRateModal
                  isOpen={editFallbackRateModalOpen}
                  fallbackMethods={transformedFallbackMethods}
                  mode={'edit'}
                  onClose={() => {
                    setEditFallbackRateModalOpen(false);
                  }}
                  onCreateSuccess={async (requests: FallbackRequest[]) => {
                    try {
                      await Promise.all(
                        requests.map(async (request) => {
                          request.checkoutId = props.activeCheckout.id;
                          await props.checkoutActions.enableFallback(request);
                        }),
                      );
                      setEnabledFallbackToastMessage('Fallback rate is on');
                    } catch (error) {
                      setEnabledFallbackToastMessage('Failed to enable fallback rates. Please try again.');
                    } finally {
                      setEditFallbackRateModalOpen(false);
                    }
                  }}
                  currencySign={shopCurrency}
                />
              )}

              <RemoveFallbackRateModal
                isOpen={removeFallbackModalOpen}
                onClose={() => {
                  setRemoveFallbackModalOpen(false);
                }}
                onDeleteSuccess={async () => {
                  try {
                    await Promise.all(
                      fallbackMethods.map(
                        async (method) => await props.checkoutActions.disableFallback(parseInt(method.id)),
                      ),
                    );
                    setEnabledFallbackToastMessage('Fallback rate is off');
                  } catch (error) {
                    setEnabledFallbackToastMessage('Failed to disable fallback rates. Please try again.');
                  } finally {
                    setRemoveFallbackModalOpen(false);
                  }
                }}
              />

              <Button
                text="Edit"
                onClick={() => setEditFallbackRateModalOpen(true)}
                size="medium"
                variant="secondary"
              />
            </div>
          </div>
          <DeliveryMethodsTableHeader
            allSelected={allFilteredSelected}
            someSelected={someFilteredSelected}
            onSelectAll={handleSelectAll}
            disabled={filteredDeliveryMethods.length === 0}
          />
        </div>
        <div className="delivery-methods-list__main-content__dm-list">
          {isGrouped && categoryConfig ? (
            <GroupedDeliveryMethods
              methods={filteredDeliveryMethods}
              config={categoryConfig}
              shopCurrency={shopCurrency}
              onCategoryFallbackChange={handleCategoryFallbackChange}
              renderMethodRow={renderMethodRow}
            />
          ) : (
            <DeliveryMethodSections
              displayOrder={displayOrder}
              deliveryMethodById={deliveryMethodById}
              isMethodVisible={filterMethod}
              isFilterActive={isFilterActive}
              selectedMethodIds={selectedIds}
              isCreatingSection={isCreatingSection}
              onFinishCreatingSection={() => setIsCreatingSection(false)}
              collapsedSectionKeys={collapsedSectionKeys}
              onToggleSectionCollapsed={toggleSectionCollapsed}
              applyDisplayOrderChange={applyDisplayOrderChange}
              renderMethodRow={renderMethodRow}
            />
          )}
          <ConfirmationModal
            title={'Are you sure?'}
            buttonText={'Delete'}
            mainContent={`You're deleting the delivery method ${deleteDeliveryMethod?.name}`}
            isOpen={deleteDeliveryMethod != null}
            onClose={() => {
              setDeleteDeliveryMethod(null);
            }}
            onSuccess={async () => {
              if (deleteDeliveryMethod) {
                await props.checkoutActions.deleteDeliveryMethod(parseInt(deleteDeliveryMethod.id));
                setSelectedIds((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(deleteDeliveryMethod.id);
                  return newSet;
                });
              }
              setDeleteDeliveryMethod(null);
            }}
          />

          <DuplicateMethodToAnotherCheckoutGroupModal
            checkouts={props.checkoutGroups.filter((checkout) => checkout.id !== props.activeCheckout.id)}
            isOpen={duplicateModalOpen}
            onClose={() => {
              setDuplicateModalOpen(false);
            }}
            onSuccess={async (selectedCheckoutIds: number[]) => {
              if (deliveryMethodToDuplicate) {
                props.checkoutActions.duplicateMethodToOtherCheckoutGroups(
                  parseInt(deliveryMethodToDuplicate.id),
                  selectedCheckoutIds,
                );
              }
              setDuplicateModalOpen(false);
            }}
            deliveryMethodName={deliveryMethodToDuplicate?.name || ''}
          />
        </div>
      </div>

      <BulkEditModal
        isOpen={bulkEditModalOpen}
        selectedCount={selectedIds.size}
        onClose={() => setBulkEditModalOpen(false)}
        onSaveComplete={(affectedGroupIds) => {
          setSelectedIds(new Set());
          if (affectedGroupIds?.length) {
            if (props.onInvalidateGroups) {
              props.onInvalidateGroups(affectedGroupIds);
            }
            affectedGroupIds.forEach((groupId) => {
              props.refetchGroupShippings?.(groupId);
            });
          }
        }}
        onDeselectMethods={(methodIds) => {
          const newSelectedIds = new Set(selectedIds);
          methodIds.forEach((id) => newSelectedIds.delete(id));
          setSelectedIds(newSelectedIds);
        }}
        onRestoreMethods={(methodIds) => {
          setSelectedIds(new Set(methodIds));
        }}
        currencySign={shopCurrency}
        selectedMethods={selectedMethodsInfo}
        shopCountryCode={shopCountryCode}
        allShippingMethods={allShippingMethods}
        moveToSection={
          isGrouped ? undefined : { sections: displayOrder.sections, onMoveToSection: moveSelectedMethodsToSection }
        }
      />
      {hasUnsavedDisplayOrderChangesOrSectionBeingCreated &&
        headerPortalElement &&
        createPortal(
          <div className="delivery-methods-list__unsaved-order-actions">
            <Button
              text="Cancel"
              onClick={discardDisplayOrderChanges}
              size="medium"
              variant="secondary"
              disabled={isSavingDisplayOrder}
            />
            <Button
              text={isSavingDisplayOrder ? 'Saving...' : 'Save'}
              onClick={saveDisplayOrder}
              size="medium"
              variant="primary"
              disabled={isSavingDisplayOrder}
            />
          </div>,
          headerPortalElement,
        )}
    </div>
  );
};
